// src/components/tabs/TabPane.tsx
// One tab's page, mounted in a router of its own.
//
// Nesting a router inside the app's BrowserRouter is normally an invariant
// error: Router calls useInRouterContext(), which is `useContext(LocationContext)
// != null`. Providing null for that context resets the check, which is what lets
// each tab keep an independent history — and why pages need no changes at all:
// useNavigate, useParams, useLocation and Link inside them address their own tab.
//
// Pinned to react-router 6.30; the UNSAFE_ prefix is the library's marker for
// internals, so a major upgrade must re-verify this.

import { useEffect, useRef } from 'react';
import {
  MemoryRouter,
  UNSAFE_LocationContext,
  useLocation,
  useNavigationType,
  useNavigate,
} from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { AppRoutes } from '@/routes/AppRoutes';
import { TabIdProvider } from './TabContext';
import { tabsStore } from '@/stores/TabsStore';
import { entityStore } from '@/stores/EntityStore';
import { resolveTitle } from '@/features/tabs/logic';
import { PaneControlsProvider, useNavbarSlot } from '@/features/tabs/PaneChrome';
import { PaneSurfaceProvider, usePaneSurface } from '@/features/tabs/PaneSurface';
import type { ContentAlign, ContentWidth } from '@/features/tabs/types';

/**
 * Reports the pane's own location back to the store, so the tab title follows
 * navigation and a reload restores where the user actually was.
 */
const LocationReporter = observer(({ tabId, isFocused, reportPaneLocation }: { tabId: string; isFocused: boolean; reportPaneLocation: (tabId: string, path: string, replace: boolean) => void }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { t } = useI18n();
  const previousPath = useRef(location.pathname + location.search);

  // Read the entity in the component body (the component is an observer), not
  // only inside the effect: the store fills it asynchronously after navigation,
  // and a title resolved from an empty context would stay the raw id forever.
  const entity = entityStore.stateFor(tabId).currentEntity;

  useEffect(() => {
    const path = location.pathname + location.search;
    const changed = previousPath.current !== path;
    previousPath.current = path;
    tabsStore.updateTabLocation(tabId, path, resolveTitle(location.pathname, t, entity));
    if (tabsStore.pendingNavigation?.tabId === tabId) {
      tabsStore.completeNavigation(tabId, path);
      return;
    }
    // A navigation within the focused pane makes a browser history entry.
    // Focus changes only replace the current entry.
    if (isFocused) {
      reportPaneLocation(tabId, path, !changed || navigationType !== 'PUSH');
    }
  }, [location.pathname, location.search, navigationType, tabId, isFocused, t, entity, reportPaneLocation]);

  return null;
});

const PaneNavigationBridge = observer(({ tabId }: { tabId: string }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pending = tabsStore.pendingNavigation;
  useEffect(() => {
    if (pending?.tabId !== tabId) return;
    if (location.pathname + location.search === pending.path) {
      tabsStore.completeNavigation(tabId, pending.path);
    } else {
      navigate(pending.path, { replace: true });
    }
  }, [tabId, pending, location.pathname, location.search, navigate]);
  return null;
});

function widthClass(width: ContentWidth, align: ContentAlign): string {
  if (width === 'full') return 'w-full';
  const alignment =
    align === 'left' ? 'mr-auto' : align === 'right' ? 'ml-auto' : 'mx-auto';
  return `w-full max-w-4xl ${alignment}`;
}

interface TabPaneProps {
  tabId: string;
  path: string;
  isActive: boolean;
  isFocused: boolean;
  width: ContentWidth;
  align: ContentAlign;
  reportPaneLocation: (tabId: string, path: string, replace: boolean) => void;
}

/**
 * An inactive pane is hidden rather than unmounted, which is what keeps scroll
 * position, filters and half-filled forms alive across tab switches.
 */
export const TabPane = observer(
  ({ tabId, path, isActive, isFocused, width, align, reportPaneLocation }: TabPaneProps) => {
    // The router is created once per tab: re-creating it on every path change
    // would throw away the pane's history and its pages' state.
    const initialPath = useRef(path);
    const navbarSlot = useNavbarSlot();

    // A page that claims the whole surface gets the bare rectangle: the padding
    // and the width cap are the pane's way of presenting a document, and a page
    // that is itself an application has to lay out its own edges. It also takes
    // over scrolling, since it scrolls regions rather than the page.
    const { fullSurface, surface } = usePaneSurface();

    return (
      // The page inside writes its entity context and quick actions under this
      // id, so a pane in the background cannot overwrite what the pane in front
      // put on the rail.
      <TabIdProvider value={tabId}>
        <div
          className={
            !isActive
              ? 'hidden'
              : fullSurface
                ? 'flex flex-1 min-h-0 flex-col overflow-hidden'
                : 'flex-1 min-h-0 overflow-y-auto'
          }
          data-tab-pane={tabId}
          data-full-surface={isActive && fullSurface ? '' : undefined}
          aria-hidden={!isActive}
        >
          {/* pane-container makes this the container pages measure
              themselves against, so a page in a narrow split lays itself out for
              the split and not for the window. */}
          <div
            className={
              fullSurface
                ? 'pane-container flex min-h-0 flex-1 flex-col'
                : `pane-container ${widthClass(width, align)} px-6 py-4`
            }
          >
            {/* The navbar serves the page being worked in: the visible tab of
                the focused pane. Dormant tabs and the panes beside this one are
                still mounted and would otherwise all paint into that one row. */}
            <PaneControlsProvider value={isActive && isFocused ? navbarSlot : null}>
              <UNSAFE_LocationContext.Provider value={null as never}>
                <MemoryRouter initialEntries={[initialPath.current]}>
                  <PaneSurfaceProvider surface={surface}>
                    <PaneNavigationBridge tabId={tabId} />
                    <LocationReporter tabId={tabId} isFocused={isFocused && isActive} reportPaneLocation={reportPaneLocation} />
                    <AppRoutes />
                  </PaneSurfaceProvider>
                </MemoryRouter>
              </UNSAFE_LocationContext.Provider>
            </PaneControlsProvider>
          </div>
        </div>
      </TabIdProvider>
    );
  },
);
